import React, { useEffect, useState, useRef, Children, Component } from 'react';
import { motion, useInView, useSpring, useTransform, Variants } from 'framer-motion';
// Organic easing curve - mimics natural growth
export const organicEase = [0.43, 0.13, 0.23, 0.96] as const;
// Mycelium teal color
export const myceliumTeal = 'rgb(43, 199, 183)';
export const myceliumTealGlow = 'rgba(43, 199, 183, 0.6)';
// Staggered text reveal with organic timing
interface MyceliumTextProps {
  children: string;
  className?: string;
  as?: 'h1' | 'h2' | 'h3' | 'p' | 'span';
  delay?: number;
  staggerChildren?: number;
  glowOnReveal?: boolean;
}
export function MyceliumText({
  children,
  className = '',
  as: Component = 'span',
  delay = 0,
  staggerChildren = 0.03,
  glowOnReveal = true
}: MyceliumTextProps) {
  const words = children.split(' ');
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  const containerVariants: Variants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren,
        delayChildren: delay
      }
    }
  };
  const wordVariants: Variants = {
    hidden: {
      opacity: 0,
      y: 20,
      filter: 'blur(4px)'
    },
    visible: {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: {
        duration: 0.6,
        ease: organicEase
      }
    }
  };
  const MotionComponent = motion[Component] as any;
  return <MotionComponent ref={ref} className={`${className} ${glowOnReveal ? 'mycelium-text-glow' : ''}`} variants={containerVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} style={{
    display: 'inline-block'
  }} data-id="element-151">
      {words.map((word, i) => <motion.span key={i} variants={wordVariants} style={{
      display: 'inline-block',
      marginRight: '0.25em'
    }} data-id="element-152">
          {word}
        </motion.span>)}
    </MotionComponent>;
}
// Pulsing node - like mycelium network nodes
interface PulsingNodeProps {
  size?: number;
  delay?: number;
  className?: string;
  intensity?: 'low' | 'medium' | 'high';
}
export function PulsingNode({
  size = 8,
  delay = 0,
  className = '',
  intensity = 'medium'
}: PulsingNodeProps) {
  const glowIntensity = {
    low: '0 0 8px rgba(43, 199, 183, 0.4)',
    medium: '0 0 12px rgba(43, 199, 183, 0.6)',
    high: '0 0 20px rgba(43, 199, 183, 0.8)'
  };
  return <motion.div className={`rounded-full bg-vitalityTeal ${className}`} style={{
    width: size,
    height: size
  }} initial={{
    scale: 0,
    opacity: 0
  }} animate={{
    scale: [1, 1.2, 1],
    opacity: [0.6, 1, 0.6],
    boxShadow: ['0 0 4px rgba(43, 199, 183, 0.3)', glowIntensity[intensity], '0 0 4px rgba(43, 199, 183, 0.3)']
  }} transition={{
    delay,
    duration: 2 + Math.random() * 0.5,
    repeat: Infinity,
    ease: 'easeInOut'
  }} data-id="element-153" />;
}
// Growing line - like mycelium branches
interface MyceliumLineProps {
  direction?: 'vertical' | 'horizontal';
  length?: number | string;
  thickness?: number;
  delay?: number;
  className?: string;
}
export function MyceliumLine({
  direction = 'vertical',
  length = 100,
  thickness = 2,
  delay = 0,
  className = ''
}: MyceliumLineProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-20px'
  });
  const isVertical = direction === 'vertical';
  return <motion.div ref={ref} className={`bg-vitalityTeal/30 ${className}`} style={{
    width: isVertical ? thickness : typeof length === 'number' ? `${length}px` : length,
    height: isVertical ? typeof length === 'number' ? `${length}px` : length : thickness,
    boxShadow: '0 0 8px rgba(43, 199, 183, 0.3)',
    transformOrigin: isVertical ? 'top' : 'left'
  }} initial={{
    scaleX: isVertical ? 1 : 0,
    scaleY: isVertical ? 0 : 1,
    opacity: 0
  }} animate={isInView ? {
    scaleX: 1,
    scaleY: 1,
    opacity: 1
  } : {
    scaleX: isVertical ? 1 : 0,
    scaleY: isVertical ? 0 : 1,
    opacity: 0
  }} transition={{
    delay,
    duration: 0.8,
    ease: organicEase
  }} data-id="element-154" />;
}
// SVG Growing Path - organic branch growth
interface GrowingPathProps {
  path: string;
  className?: string;
  delay?: number;
  duration?: number;
  strokeWidth?: number;
}
export function GrowingPath({
  path,
  className = '',
  delay = 0,
  duration = 1.5,
  strokeWidth = 2
}: GrowingPathProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  return <svg ref={ref} className={`overflow-visible ${className}`} data-id="element-155">
      <motion.path d={path} fill="none" stroke="url(#myceliumGradient)" strokeWidth={strokeWidth} strokeLinecap="round" initial={{
      pathLength: 0,
      opacity: 0
    }} animate={isInView ? {
      pathLength: 1,
      opacity: 1
    } : {
      pathLength: 0,
      opacity: 0
    }} transition={{
      pathLength: {
        delay,
        duration,
        ease: organicEase
      },
      opacity: {
        delay,
        duration: 0.3
      }
    }} style={{
      filter: 'drop-shadow(0 0 6px rgba(43, 199, 183, 0.5))'
    }} data-id="element-156" />
      <defs data-id="element-157">
        <linearGradient id="myceliumGradient" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-158">
          <stop offset="0%" stopColor="rgba(43, 199, 183, 0.3)" data-id="element-159" />
          <stop offset="50%" stopColor="rgba(43, 199, 183, 0.8)" data-id="element-160" />
          <stop offset="100%" stopColor="rgba(43, 199, 183, 0.3)" data-id="element-161" />
        </linearGradient>
      </defs>
    </svg>;
}
// Count up animation with glow
interface CountUpProps {
  end: number;
  prefix?: string;
  suffix?: string;
  duration?: number;
  delay?: number;
  className?: string;
}
export function CountUp({
  end,
  prefix = '',
  suffix = '',
  duration = 2,
  delay = 0,
  className = ''
}: CountUpProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  const [hasAnimated, setHasAnimated] = useState(false);
  const spring = useSpring(0, {
    duration: duration * 1000,
    bounce: 0
  });
  const display = useTransform(spring, current => {
    if (end >= 1000000000) {
      return `${(current / 1000000000).toFixed(1)}B`;
    }
    if (end >= 1000000) {
      return `${(current / 1000000).toFixed(1)}M`;
    }
    if (end >= 1000) {
      return `${(current / 1000).toFixed(1)}K`;
    }
    return Math.floor(current).toString();
  });
  useEffect(() => {
    if (isInView && !hasAnimated) {
      const timeout = setTimeout(() => {
        spring.set(end);
        setHasAnimated(true);
      }, delay * 1000);
      return () => clearTimeout(timeout);
    }
  }, [isInView, hasAnimated, spring, end, delay]);
  return <motion.span ref={ref} className={className} initial={{
    opacity: 0,
    scale: 0.8
  }} animate={isInView ? {
    opacity: 1,
    scale: 1
  } : {}} transition={{
    delay,
    duration: 0.5,
    ease: organicEase
  }} style={{
    textShadow: isInView ? '0 0 20px rgba(43, 199, 183, 0.5)' : 'none'
  }} data-id="element-162">
      {prefix}
      <motion.span data-id="element-163">{display}</motion.span>
      {suffix}
    </motion.span>;
}
// Staggered container for lists
interface StaggerContainerProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  staggerDelay?: number;
}
export function StaggerContainer({
  children,
  className = '',
  delay = 0,
  staggerDelay = 0.1
}: StaggerContainerProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  return <motion.div ref={ref} className={className} initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={{
    hidden: {},
    visible: {
      transition: {
        staggerChildren: staggerDelay,
        delayChildren: delay
      }
    }
  }} data-id="element-164">
      {children}
    </motion.div>;
}
// Stagger item for use within StaggerContainer
interface StaggerItemProps {
  children: React.ReactNode;
  className?: string;
}
export function StaggerItem({
  children,
  className = ''
}: StaggerItemProps) {
  return <motion.div className={className} variants={{
    hidden: {
      opacity: 0,
      x: -20,
      filter: 'blur(4px)'
    },
    visible: {
      opacity: 1,
      x: 0,
      filter: 'blur(0px)',
      transition: {
        duration: 0.5,
        ease: organicEase
      }
    }
  }} data-id="element-165">
      {children}
    </motion.div>;
}
// Glow wrapper for any element
interface GlowWrapperProps {
  children: React.ReactNode;
  className?: string;
  glowColor?: string;
  intensity?: 'low' | 'medium' | 'high';
  pulse?: boolean;
}
export function GlowWrapper({
  children,
  className = '',
  glowColor = 'rgba(43, 199, 183, 0.4)',
  intensity = 'medium',
  pulse = false
}: GlowWrapperProps) {
  const blurAmount = {
    low: 8,
    medium: 15,
    high: 25
  };
  return <motion.div className={`relative ${className}`} animate={pulse ? {
    boxShadow: [`0 0 ${blurAmount[intensity]}px ${glowColor}`, `0 0 ${blurAmount[intensity] * 1.5}px ${glowColor}`, `0 0 ${blurAmount[intensity]}px ${glowColor}`]
  } : {}} transition={pulse ? {
    duration: 2,
    repeat: Infinity,
    ease: 'easeInOut'
  } : {}} style={{
    boxShadow: `0 0 ${blurAmount[intensity]}px ${glowColor}`
  }} data-id="element-166">
      {children}
    </motion.div>;
}
// Floating animation wrapper
interface FloatingWrapperProps {
  children: React.ReactNode;
  className?: string;
  amplitude?: number;
  duration?: number;
}
export function FloatingWrapper({
  children,
  className = '',
  amplitude = 10,
  duration = 4
}: FloatingWrapperProps) {
  return <motion.div className={className} animate={{
    y: [-amplitude / 2, amplitude / 2, -amplitude / 2]
  }} transition={{
    duration: duration + Math.random(),
    repeat: Infinity,
    ease: 'easeInOut'
  }} data-id="element-167">
      {children}
    </motion.div>;
}
// Section connector - organic line between sections
interface SectionConnectorProps {
  className?: string;
}
export function SectionConnector({
  className = ''
}: SectionConnectorProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <div ref={ref} className={`flex flex-col items-center py-8 ${className}`} data-id="element-168">
      <motion.div className="w-0.5 bg-gradient-to-b from-transparent via-vitalityTeal/50 to-transparent" initial={{
      height: 0,
      opacity: 0
    }} animate={isInView ? {
      height: 80,
      opacity: 1
    } : {}} transition={{
      duration: 1,
      ease: organicEase
    }} style={{
      boxShadow: '0 0 10px rgba(43, 199, 183, 0.4)'
    }} data-id="element-169" />
      <PulsingNode size={10} delay={0.5} intensity="high" data-id="element-170" />
      <motion.div className="w-0.5 bg-gradient-to-b from-transparent via-vitalityTeal/50 to-transparent" initial={{
      height: 0,
      opacity: 0
    }} animate={isInView ? {
      height: 80,
      opacity: 1
    } : {}} transition={{
      duration: 1,
      delay: 0.3,
      ease: organicEase
    }} style={{
      boxShadow: '0 0 10px rgba(43, 199, 183, 0.4)'
    }} data-id="element-171" />
    </div>;
}
// Image reveal with organic glow
interface MyceliumImageProps {
  src: string;
  alt: string;
  className?: string;
  delay?: number;
}
export function MyceliumImage({
  src,
  alt,
  className = '',
  delay = 0
}: MyceliumImageProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  return <motion.div ref={ref} className={`relative overflow-hidden ${className}`} initial={{
    opacity: 0,
    scale: 1.05
  }} animate={isInView ? {
    opacity: 1,
    scale: 1
  } : {}} transition={{
    delay,
    duration: 1.2,
    ease: organicEase
  }} data-id="element-172">
      <motion.img src={src} alt={alt} className="w-full h-full object-cover" initial={{
      scale: 1.1
    }} animate={isInView ? {
      scale: 1
    } : {}} transition={{
      delay,
      duration: 1.5,
      ease: organicEase
    }} data-id="element-173" />
      <motion.div className="absolute inset-0 pointer-events-none" initial={{
      opacity: 0
    }} animate={isInView ? {
      opacity: [0, 0.3, 0]
    } : {}} transition={{
      delay: delay + 0.2,
      duration: 1
    }} style={{
      boxShadow: 'inset 0 0 60px rgba(43, 199, 183, 0.3)'
    }} data-id="element-174" />
    </motion.div>;
}
// Reveal wrapper with glow effect
interface RevealWrapperProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  direction?: 'up' | 'down' | 'left' | 'right';
}
export function RevealWrapper({
  children,
  className = '',
  delay = 0,
  direction = 'up'
}: RevealWrapperProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  const directionOffset = {
    up: {
      y: 30,
      x: 0
    },
    down: {
      y: -30,
      x: 0
    },
    left: {
      y: 0,
      x: 30
    },
    right: {
      y: 0,
      x: -30
    }
  };
  return <motion.div ref={ref} className={className} initial={{
    opacity: 0,
    ...directionOffset[direction],
    filter: 'blur(4px)'
  }} animate={isInView ? {
    opacity: 1,
    y: 0,
    x: 0,
    filter: 'blur(0px)'
  } : {
    opacity: 0,
    ...directionOffset[direction],
    filter: 'blur(4px)'
  }} transition={{
    delay,
    duration: 0.8,
    ease: organicEase
  }} data-id="element-175">
      {children}
    </motion.div>;
}